import {useEffect, useMemo} from 'react';
import {useSearchParams} from "react-router-dom";

export const DOTS = '...';

const range = (start, end) => {
    let length = end - start + 1;
    return Array.from({length}, (_, idx) => idx + start);
};

export const usePagination = (totalCount, paramSize, paramIndex) => {

    const [searchParams, setSearchParams] = useSearchParams()

    const pageSize = Number(searchParams.get(paramSize))
    const pageIndex = Number(searchParams.get(paramIndex))

    const siblingCount = 1

    function onPageChange(index = null, size = null) {
        setSearchParams(prev => {
            if (index != null)
                prev.set(paramIndex, index)
            if (size != null)
                prev.set(paramSize, size)
            return prev
        }, {replace: true})
    }

    useEffect(() => {
        // TODO. First set initial page size accordingly to user's settings, now set to 10
        const tempPageSize = pageSize || 10
        onPageChange(null, tempPageSize)

        if (totalCount != null) {
            if (totalCount > pageIndex * tempPageSize)
                onPageChange(pageIndex, null)
            else if (totalCount > (pageIndex - 1) * tempPageSize && pageIndex > 1)
                onPageChange(pageIndex - 1, null)
            else
                onPageChange(0, null)
        }
    }, [totalCount, pageSize])

    const pages = useMemo(() => {
        const totalPageCount = Math.ceil(totalCount / pageSize);

        // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
        const totalPageNumbers = siblingCount + 5;

        /*
          If the number of pages is less than the page numbers we want to show in our
          paginationComponent, we return the range [1..totalPageCount]
        */
        if (totalPageNumbers >= totalPageCount) {
            return range(1, totalPageCount);
        }

        const leftSiblingIndex = Math.max(pageIndex - siblingCount, 1);
        const rightSiblingIndex = Math.min(pageIndex + siblingCount, totalPageCount);

        /*
          We do not want to show dots if there is only one position left
          after/before the left/right page count as that would lead to a change if our Pagination
          component size which we do not want
        */
        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

        const firstPageIndex = 1;
        const lastPageIndex = totalPageCount;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            let leftItemCount = 3 + 2 * siblingCount;
            let leftRange = range(1, leftItemCount);

            return [...leftRange, DOTS, totalPageCount];
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
            let rightItemCount = 3 + 2 * siblingCount;
            let rightRange = range(
                totalPageCount - rightItemCount + 1,
                totalPageCount
            );
            return [firstPageIndex, DOTS, ...rightRange];
        }

        if (shouldShowLeftDots && shouldShowRightDots) {
            let middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
        }
    }, [totalCount, pageSize, siblingCount, pageIndex])

    return [pages, pageIndex, pageSize, onPageChange]
}

export function usePagination2(totalCount, paramSize = "size", paramIndex = "index") {
    const defaultPageSize = 10
    const defaultPageIndex = 0

    const [searchParams, setSearchParams] = useSearchParams()

    const pageSize = Number(searchParams.get(paramSize)) || defaultPageSize
    const pageIndex = Number(searchParams.get(paramIndex)) || defaultPageIndex

    const siblingCount = 1

    function onPageChange(index, size) {
        setSearchParams(prev => {
            if (index != null) {
                if (index === defaultPageIndex) {
                    prev.delete(paramIndex)
                } else {
                    prev.set(paramIndex, index)
                }
            }
            if (size != null) {
                if (size === defaultPageSize) {
                    prev.delete(paramSize)
                } else {
                    prev.set(paramSize, size)
                }
            }
            return prev
        }, {replace: true})
    }

    const pages = useMemo(() => {
        const totalPageCount = Math.ceil(totalCount / pageSize);

        // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
        const totalPageNumbers = siblingCount + 5;

        /*
          If the number of pages is less than the page numbers we want to show in our
          paginationComponent, we return the range [1..totalPageCount]
        */
        if (totalPageNumbers >= totalPageCount) {
            return range(1, totalPageCount);
        }

        const leftSiblingIndex = Math.max(pageIndex - siblingCount, 1);
        const rightSiblingIndex = Math.min(pageIndex + siblingCount, totalPageCount);

        /*
          We do not want to show dots if there is only one position left
          after/before the left/right page count as that would lead to a change if our Pagination
          component size which we do not want
        */
        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

        const firstPageIndex = 1;
        const lastPageIndex = totalPageCount;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            let leftItemCount = 3 + 2 * siblingCount;
            let leftRange = range(1, leftItemCount);

            return [...leftRange, DOTS, totalPageCount];
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
            let rightItemCount = 3 + 2 * siblingCount;
            let rightRange = range(
                totalPageCount - rightItemCount + 1,
                totalPageCount
            );
            return [firstPageIndex, DOTS, ...rightRange];
        }

        if (shouldShowLeftDots && shouldShowRightDots) {
            let middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
        }
    }, [totalCount, pageSize, siblingCount, pageIndex])

    return [pages, pageIndex, pageSize, onPageChange]
}